import { useState } from 'react';
import type { Tarief, TariefPeriode } from '@fleet/shared';
import { euro, fmtDate, perMaand, tariefHistorie, tariefOpDatum } from '@fleet/shared';

const PERIODE_LABEL: Record<TariefPeriode, string> = {
  MAAND: 'Per maand',
  KWARTAAL: 'Per kwartaal',
  JAAR: 'Per jaar',
};

/**
 * The amount of one fixed cost that applies **today**, as rows in the same rhythm
 * as the section around it, with the history behind a `▸ Historie (n)` row.
 *
 * "Today" and not "the open row": entering next year's assessment closes the
 * current row at that future date and makes the new one open, so keying off the
 * open row would headline a rate that is not being charged yet while the rate
 * actually in force dropped into the collapsed history. The TCO chart prices each
 * month with `vasteLastenInMaand`, so the card would have contradicted the chart
 * on the same page.
 *
 * Both the billed amount and its monthly equivalent are shown when they differ:
 * road tax is assessed per quarter, which is how you know it, but per month is
 * what compares to everything else and to the charts.
 */
export function TariefBlok({
  soort,
  tarieven,
  leeg = 'Nog niet vastgelegd',
  vandaag = new Date().toISOString().slice(0, 10),
}: {
  soort: string;
  /** All rates of this car; this component picks out its own soort. */
  tarieven: Tarief[];
  /** What to show when this car has no rate of this kind yet. */
  leeg?: string;
  /** Overridable for tests. */
  vandaag?: string;
}) {
  const [open, setOpen] = useState(false);
  const huidig = tariefOpDatum(tarieven, soort, vandaag);
  const historie = tariefHistorie(tarieven, soort);
  // Wat vandaag geldt staat bovenaan. Daaronder de historie: alles wat eerder
  // gold, nieuwste eerst. Een periode die nog niet begonnen is hoort in geen van
  // beide en krijgt zijn eigen regel.
  const eerder = historie
    .filter((w) => w.tarief.id !== huidig?.id && w.tarief.geldigVan <= vandaag)
    .reverse();
  const toekomst = historie.filter((w) => w.tarief.geldigVan > vandaag);

  if (!huidig) {
    return <Rij label="Bedrag" waarde={leeg} />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
      <Rij label={PERIODE_LABEL[huidig.periode]} waarde={euro(huidig.bedrag)} />
      {huidig.periode !== 'MAAND' && (
        <Rij label="Per maand" waarde={euro(perMaand(huidig))} zacht />
      )}
      <Rij label="Geldig sinds" waarde={fmtDate(huidig.geldigVan)} zacht />
      {huidig.geldigTot && <Rij label="Loopt tot" waarde={fmtDate(huidig.geldigTot)} zacht />}
      {toekomst.map((w) => (
        <Rij
          key={w.tarief.id}
          label={`Vanaf ${fmtDate(w.tarief.geldigVan)}`}
          waarde={euro(w.tarief.bedrag)}
          zacht
        />
      ))}

      {eerder.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              fontSize: 12,
              fontWeight: 700,
              color: '#2A6085',
              cursor: 'pointer',
              fontFamily: 'inherit',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            <span style={{ fontSize: 10 }}>{open ? '▾' : '▸'}</span>
            Historie ({eerder.length})
          </button>
          {open &&
            eerder.map((w) => (
              <div
                key={w.tarief.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 10,
                  paddingLeft: 15,
                }}
              >
                <span style={{ fontSize: 12, color: '#888888' }}>
                  {fmtDate(w.tarief.geldigVan)}
                  {w.tarief.geldigTot ? ` – ${fmtDate(w.tarief.geldigTot)}` : ''}
                </span>
                <span
                  style={{ fontSize: 12.5, color: '#111111', fontWeight: 600, textAlign: 'right' }}
                >
                  {euro(w.tarief.bedrag)}
                  {w.deltaPerMaand != null && Math.abs(w.deltaPerMaand) >= 0.005 && (
                    <span
                      style={{
                        marginLeft: 6,
                        fontWeight: 700,
                        color: w.deltaPerMaand > 0 ? '#B0543B' : '#3E7D63',
                      }}
                    >
                      {w.deltaPerMaand > 0 ? '+' : '−'}
                      {euro(Math.abs(w.deltaPerMaand))}/mnd
                    </span>
                  )}
                </span>
              </div>
            ))}
        </>
      )}
    </div>
  );
}

/** Same label/value row as `CarInfoPanel`, so the two line up in one column. */
function Rij({ label, waarde, zacht }: { label: string; waarde: string; zacht?: boolean }) {
  return (
    <div
      style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}
    >
      <span style={{ fontSize: 12.5, color: '#888888' }}>{label}</span>
      <span
        style={{
          fontSize: zacht ? 13 : 14,
          color: zacht ? '#666666' : '#111111',
          fontWeight: zacht ? 500 : 600,
          textAlign: 'right',
        }}
      >
        {waarde}
      </span>
    </div>
  );
}
